import React, { useContext, useEffect, useState } from 'react';
import styles from './farmOverviewPage.module.scss';
import { Card, Col, Row } from 'antd';
import { ReactComponent as Check } from 'assets/icons/ticket_closed.svg';
import FarmboxCommodityDistributionService from 'services/Farmbox/CommodityDistribution/commodityDistribution.service';
import { useParams, useSearchParams } from 'react-router-dom';
import { SocketContext } from 'context/SocketContext';
import { CommodityOverview } from 'models/Farmbox/overview.model';
import { initSocket } from 'shared/utils/initSocket';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import { STAGE0 } from 'constants/FarmboxConstants/stage0.constants';
import { useYearLabel } from 'shared/utils/generateYearSpan';
import useRedirect from 'shared/utils/useRedirect';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import SubmitApprovalModal from 'shared/components/SubmitApprovalModal';
import { stage5Fields } from 'constants/FarmboxConstants/stage5.constants';
import { SubmitApprovalModalViewProps } from 'shared/components/SubmitApprovalModal/SubmitApprovalConstants';
import FarmIcon from 'assets/icons/farm.svg';
import { deserialize } from 'serializr';
import { rawMaterial } from 'constants/constants';
import {
  IN_REVIEW_ROUTE,
  inReviewStatus,
} from 'views/FarmProductPage/constant';
import { ReactComponent as Truck } from 'assets/icons/transportation.svg';
import { ReactComponent as RawMaterial } from 'assets/icons/raw_material.svg';
import { ReactComponent as FuelAndEnergy } from 'assets/icons/processing_factor.svg';
import { ReactComponent as CommodityDistribution } from 'assets/icons/distribution.svg';
import { ReactComponent as Field } from 'assets/icons/field.svg';
import FarmHeader from './FarmHeader';
import { TabsIndex, TabsLabels } from 'enums/ProductsViewEnums';
import { useUserRole } from 'shared/hooks/useUserRole';
import { isViewer } from 'shared/utils/roleUtils';

const FarmOverviewPage = () => {
  const { setSocketConnection, socket } = useContext(SocketContext);
  const {
    loading,
    getFarmboxOverview,
    farmboxOverview,
    setFarmboxOverview,
    downloadApprovalReport,
  } = FarmboxCommodityDistributionService();
  const { navigateBack } = useRedirect();
  const { redirectToFarm, redirectToRespectiveCommodityStage } =
    useFarmboxRedirections();
  const [approvalModalState, setApprovalModalState] = useState<boolean>(false);
  const { id: commodityId, status } = useParams();
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  const userRole = useUserRole();
  const isViewerRole = isViewer(userRole);
  const approvalHandler = () => setApprovalModalState(true);
  const handleCloseModal = () => setApprovalModalState(false);
  const { LABELS } = STAGE0;
  const { COMMODITY_CHECKBOX_CONTENT, COMMODITY_CONTENT, COMMODITY_HEADING } =
    SubmitApprovalModalViewProps;
  const {
    HARVEST_YEAR,
    CROP_TYPE_NAME,
    FARM_TITLE,
    FIELD_AREA,
    CO_PRODUCTS,
    DRY_WEIGHT_UNIT,
    DRY_WEGHT_TITLE,
  } = LABELS;
  const { SOCKET_EVENT } = stage5Fields;
  useEffect(() => {
    const token = localStorage.getItem('user');
    socket?.disconnect();
    const socketDetails = initSocket(token);
    setSocketConnection(socketDetails);
  }, []);

  useEffect(() => {
    getFarmboxOverview(Number(commodityId));
    if (socket) {
      socket.on(SOCKET_EVENT, (data: { commodity: CommodityOverview }) => {
        const desiredData = deserialize(CommodityOverview, data?.commodity);
        setFarmboxOverview(desiredData);
      });
      return () => {
        socket.off(SOCKET_EVENT);
      };
    }
  }, [socket, commodityId]);

  const editNavigateHandler = () =>
    redirectToRespectiveCommodityStage(
      Number(commodityId),
      rawMaterial,
      status === inReviewStatus ? IN_REVIEW_ROUTE : status,
      queryParams
    );
  const cancelCallback = () =>
    farmboxOverview?.status &&
    redirectToFarm(
      farmboxOverview?.status === TabsLabels.IN_REVIEW_STATUS
        ? TabsIndex.IN_REVIEW
        : farmboxOverview?.status
    );

  const stageItems = [
    {
      name: 'Raw Materials',
      icon: <RawMaterial />,
      emissionValue: Number(farmboxOverview?.rawMaterialsEmission)?.toFixed(4),
    },
    {
      name: 'Raw Materials Transportation',
      icon: <Truck />,
      emissionValue: Number(farmboxOverview?.transportationEmission)?.toFixed(
        4
      ),
    },
    {
      name: 'Fuel & Energy',
      icon: <FuelAndEnergy />,
      emissionValue: Number(farmboxOverview?.scope1And2Emission)?.toFixed(4),
    },
    {
      name: 'Field',
      icon: <Field />,
      emissionValue: Number(farmboxOverview?.fieldEmission)?.toFixed(4),
    },
    {
      name: 'Commodity Distribution',
      icon: <CommodityDistribution />,
      emissionValue: Number(farmboxOverview?.distributionEmission)?.toFixed(4),
    },
  ];
  const harvestYearSpan = useYearLabel(
    farmboxOverview?.harvestYear?.toString() ?? ''
  );

  const commodityDetailsItems = [
    {
      name: HARVEST_YEAR,
      value: harvestYearSpan,
    },
    {
      name: FARM_TITLE,
      value: farmboxOverview?.farmName,
    },
    {
      name: CROP_TYPE_NAME,
      value: farmboxOverview?.cropName,
    },
    {
      name: DRY_WEGHT_TITLE,
      value: `${farmboxOverview?.dryWeight} kg`,
    },
    {
      name: FIELD_AREA,
      value: `${farmboxOverview?.area} ${farmboxOverview?.areaUnit}`,
    },
  ];

  const cradleToFarmValue =
    Number(farmboxOverview?.rawMaterialsEmission ?? 0) +
    Number(farmboxOverview?.transportationEmission ?? 0) +
    Number(farmboxOverview?.scope1And2Emission ?? 0) +
    Number(farmboxOverview?.fieldEmission ?? 0);

  const cradleToMarketValue =
    Number(cradleToFarmValue ?? 0) +
    Number(farmboxOverview?.distributionEmission ?? 0);

  const handleReport = () => downloadApprovalReport(Number(commodityId));

  return (
    <div className={styles.overviewContainer}>
      <FarmHeader
        loading={loading}
        farmboxOverview={farmboxOverview}
        harvestYearSpan={harvestYearSpan}
        navigateBack={navigateBack}
        cancelCallback={cancelCallback}
        editNavigateHandler={editNavigateHandler}
        approvalHandler={approvalHandler}
        handleReport={handleReport}
        isViewerRole={isViewerRole}
      />
      {loading ? (
        <div className={styles.loaderAnimation}>
          <LoaderAnimation />
        </div>
      ) : (
        <div className={styles.detailsContainer}>
          <div className={styles.farmDetailsContainer}>
            <div className={styles.imageContainer}>
              <div className={styles.image}>
                <img src={farmboxOverview?.imageUrl ?? FarmIcon} width={50} />
              </div>
              <div className={styles.farmName}>
                {farmboxOverview?.name}
                <span className={styles.weight}>
                  {farmboxOverview?.dryWeight} kg
                </span>
              </div>
            </div>
            <div className={styles.emissionContainer}>
              <div className={styles.emissionItem}>
                Cradle To Farm
                <span className={styles.emissionValue}>
                  {cradleToFarmValue?.toFixed(4)}
                  <span className={styles.emissionUnit}>{DRY_WEIGHT_UNIT}</span>
                </span>
              </div>
              <div className={styles.emissionItem}>
                Cradle To Market
                <span className={styles.emissionValue}>
                  {cradleToMarketValue?.toFixed(4)}
                  <span className={styles.emissionUnit}>{DRY_WEIGHT_UNIT}</span>
                </span>
              </div>
            </div>
          </div>
          <div className={styles.stageEmissionContainer}>
            <div className={styles.total}>Total</div>
            <div className={styles.emissionValueContainer}>
              <div className={styles.emissionItem}>
                {stageItems?.map(item => (
                  <div key={item.name} className={styles.itemDetails}>
                    <span>
                      <div className={styles.completedIcon}>
                        <Check />
                      </div>
                    </span>
                    <span className={styles.itemName}>
                      <div className={styles.itemDetails}>
                        <div className={styles.stageIcon}>{item?.icon}</div>
                        <div className={styles.stageName}>{item.name}</div>
                      </div>
                      <p className={styles.emissionValue}>
                        {item.emissionValue} {DRY_WEIGHT_UNIT}
                      </p>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <Card className={styles.commodityDetailsContainer}>
            <div className={styles.commodityDetailsWrapper}>
              {commodityDetailsItems?.map(item => (
                <div className={styles.details} key={item?.name}>
                  <div className={styles.detailsName}>{item?.name}</div>
                  <div className={styles.detailsValue}>{item?.value}</div>
                </div>
              ))}
            </div>
            <div className={styles.coProductsWrapper}>
              {farmboxOverview?.CoProducts?.length ? (
                <span className={styles.coProductsTitle}>{CO_PRODUCTS}</span>
              ) : null}
              <Row gutter={[20, 24]} className={styles.coProductsContainer}>
                {farmboxOverview?.CoProducts?.map(item => (
                  <Col span={6} key={item.name} className={styles.details}>
                    <div className={styles.detailsName}>{item.name}</div>
                    <div className={styles.detailsValue}>
                      {Number(item.emission)?.toFixed(4)} {DRY_WEIGHT_UNIT}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </Card>
          <SubmitApprovalModal
            showModalState={approvalModalState}
            handleModalCancel={handleCloseModal}
            handleModalConfirm={handleCloseModal}
            commodityId={commodityId}
            heading={COMMODITY_HEADING}
            content={COMMODITY_CONTENT}
            confirmationText={COMMODITY_CHECKBOX_CONTENT}
          />
        </div>
      )}
    </div>
  );
};

export default FarmOverviewPage;
